import React, { useState } from 'react';
import {
    NSCard,
    NSLabel,
    NSCheckbox,
    NSButton,
    NSInput,
    NSFormGroup,
} from 'bricks';
import classNames from 'classnames';
import { GPTCheckboxItem } from 'views/InvestmentMemo/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark, faPencil, faPlus, faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { AI_ICON_URL } from 'views/InvestmentMemo/constants';
import RenderIf from '../../components/RenderIf/RenderIf';

export interface INSGPTCardProps {
    label: string;
    checkboxContent: GPTCheckboxItem[];
    onCheckboxCheck: (index: number, checked: boolean) => void;
    onDeleteCheckbox: (id: string) => void;
    onEditCheckbox?: (id: string, name: string, description: string) => void;
    shouldAddCustomSection?: boolean;
    customSectionButtonLabel?: string;
    customNamePlaceholder?: string;
    customDescriptionPlaceholder?: string;
    onCustomItemAdd?: (name: string, description: string) => void;
    isLoading?: boolean;
    loadingMessage?: string;
}

const NSGPTCard = ({
    label,
    checkboxContent,
    onCheckboxCheck,
    onDeleteCheckbox,
    onEditCheckbox,
    shouldAddCustomSection,
    customSectionButtonLabel = 'Add custom',
    customNamePlaceholder = 'Name',
    customDescriptionPlaceholder = 'Description',
    onCustomItemAdd,
    isLoading = false,
    loadingMessage = 'Generating...',
}: INSGPTCardProps) => {
    const [isAddingCustom, setIsAddingCustom] = useState(false);
    const [customName, setCustomName] = useState('');
    const [customDescription, setCustomDescription] = useState('');
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');

    const handleAddCustomItem = () => {
        if (customName.trim() && onCustomItemAdd) {
            onCustomItemAdd(customName.trim(), customDescription.trim());
            setCustomName('');
            setCustomDescription('');
            setIsAddingCustom(false);
        }
    };

    const handleCancelAddCustom = () => {
        setCustomName('');
        setCustomDescription('');
        setIsAddingCustom(false);
    };

    const handleStartEdit = (item: GPTCheckboxItem) => {
        setEditingItemId(item.id);
        setEditName(item.name);
        setEditDescription(item.description);
    };

    const handleSaveEdit = (id: string) => {
        if (onEditCheckbox && editName.trim()) {
            onEditCheckbox(id, editName, editDescription);
        }
        setEditingItemId(null);
        setEditName('');
        setEditDescription('');
    };

    const handleCancelEdit = () => {
        setEditingItemId(null);
        setEditName('');
        setEditDescription('');
    };

    return (
        <NSCard className="NSCard--level-3 p-3 mt-3 border border-rounded ">
            <NSLabel>
                {label}
                <img
                    src={AI_ICON_URL}
                    className="NSInput__label-ai-icon rounded ml-1"
                    alt="Northspyre AI"
                />
            </NSLabel>
            <RenderIf isTrue={isLoading}>
                <div className="d-flex align-items-center justify-content-center p-2 border rounded mt-2">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    <span className="text-muted">{loadingMessage}</span>
                </div>
            </RenderIf>

            {checkboxContent.map((item, index) => {
                const isEditing = editingItemId === item.id;
                const shouldShow = !isLoading || !item.isGenerated;

                if (!shouldShow) return null;

                return (
                    <NSCard
                        key={item.id}
                        className={classNames('NSCard--level-5 p-2 mt-2 mb-1', {
                            'border border-primary bg-primary-lighten': item.isChecked,
                            'border-2 border-info': isEditing,
                        })}
                    >
                        <RenderIf isTrue={isEditing}>
                            <div className="d-flex flex-column">
                                <NSFormGroup className="mb-2">
                                    <NSInput
                                        name="editName"
                                        type="text"
                                        value={editName}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditName(e.target.value)}
                                        placeholder="Name"
                                        size="sm"
                                    />
                                </NSFormGroup>
                                <NSFormGroup className="mb-2">
                                    <NSInput
                                        name="editDescription"
                                        type="textarea"
                                        value={editDescription}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditDescription(e.target.value)}
                                        placeholder="Description"
                                        rows={2}
                                        size="sm"
                                    />
                                </NSFormGroup>
                                <div className="d-flex justify-content-end">
                                    <NSButton
                                        text="Cancel"
                                        size="sm"
                                        callback={handleCancelEdit}
                                        className="text-muted mr-2"
                                    />
                                    <NSButton
                                        text="Save"
                                        color="primary"
                                        size="sm"
                                        callback={() => handleSaveEdit(item.id)}
                                        disabled={!editName.trim()}
                                    />
                                </div>
                            </div>
                        </RenderIf>

                        <RenderIf isTrue={!isEditing}>
                            <div className="d-flex align-items-center">
                                <NSCheckbox
                                    id={item.name + item.id}
                                    checked={item.isChecked}
                                    callback={() => onCheckboxCheck(index, !item.isChecked)}
                                    containerClassName="mr-1"
                                    aria-label={item.name}
                                />
                                <div className="d-flex flex-column justify-content-center">
                                    <span className="text-white font-weight-normal">{item.name}</span>
                                    <span className="text-grey font-12 font-weight-normal">{item.description}</span>
                                </div>
                                <RenderIf isTrue={item.isGenerated && !!onEditCheckbox}>
                                    <FontAwesomeIcon
                                        icon={faPencil}
                                        onClick={() => handleStartEdit(item)}
                                        className="ml-auto cursor--pointer"
                                    />
                                </RenderIf>
                                <RenderIf isTrue={!item.isGenerated}>
                                    <FontAwesomeIcon
                                        icon={faXmark}
                                        onClick={() => onDeleteCheckbox(item.id)}
                                        className="ml-auto cursor--pointer"
                                    />
                                </RenderIf>
                            </div>
                        </RenderIf>
                    </NSCard>
                );
            })}

            <RenderIf isTrue={shouldAddCustomSection}>
                <RenderIf isTrue={!isAddingCustom}>
                    <NSButton
                        color="link"
                        size="sm"
                        callback={() => setIsAddingCustom(true)}
                        className="mt-2 p-0 text-primary"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                        {customSectionButtonLabel}
                    </NSButton>
                </RenderIf>

                <RenderIf isTrue={isAddingCustom}>
                    <NSCard className="NSCard--level-5 p-2 mt-2 mb-1 border-2 border-info">
                        <div className="d-flex flex-column">
                            <NSFormGroup className="mb-2">
                                <NSInput
                                    name="customName"
                                    type="text"
                                    value={customName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomName(e.target.value)}
                                    placeholder={customNamePlaceholder}
                                    size="sm"
                                />
                            </NSFormGroup>
                            <NSFormGroup className="mb-2">
                                <NSInput
                                    name="customDescription"
                                    type="textarea"
                                    value={customDescription}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCustomDescription(e.target.value)}
                                    placeholder={customDescriptionPlaceholder}
                                    rows={2}
                                    size="sm"
                                />
                            </NSFormGroup>
                            <div className="d-flex justify-content-end">
                                <NSButton
                                    text="Cancel"
                                    size="sm"
                                    callback={handleCancelAddCustom}
                                    className="text-muted mr-2"
                                />
                                <NSButton
                                    text="Add"
                                    color="primary"
                                    size="sm"
                                    callback={handleAddCustomItem}
                                    disabled={!customName.trim()}
                                />
                            </div>
                        </div>
                    </NSCard>
                </RenderIf>
            </RenderIf>
        </NSCard>
    );
};

export default NSGPTCard;
